import classNames from 'classnames';
import { m } from 'framer-motion';
import { useMemo, useState } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Typography from '@/components/atoms/typography/typography';
import Checkbox, {
  CheckboxProps,
} from '@/components/molecules/checkbox/checkbox';
import DualChoice, {
  DualChoiceProps,
} from '@/components/molecules/dual-choice/dual-choice';
import Pill from '@/components/molecules/pill/pill';
import Radiobutton, {
  RadiobuttonProps,
} from '@/components/molecules/radiobutton/radiobutton';
import RangeSlider, {
  RangeSliderProps,
} from '@/components/molecules/range-slider/range-slider';
import { useI18n } from '@/libraries/contexts/i18n-context';
import FilterSlideInSingleCheckbox from '../filter-slide-in-single-checkbox/filter-slide-in-single-checkbox';
import styles from './filter-slide-in-input-group.module.scss';

export enum FilterInput {
  CHECKBOX = 'checkbox',
  DUAL_CHOICE = 'dual_choice',
  RADIO = 'radio',
  SINGLE_CHECKBOX = 'single_checkbox',
  SLIDER = 'slider',
}

export interface DualChoiceSwitch {
  /** The props of the dual choice */
  dualChoiceProps: DualChoiceProps;
  /** The label of the filter */
  label: string;
  /** The type of the filter */
  type: FilterInput.DUAL_CHOICE;
}

interface InputGroupBase {
  /** Facility identifier */
  facilityId?: string;
  /** Identifier */
  id: string;
  /** The label of the filter */
  label: DualChoiceSwitch | string;
  /** Name of the filter */
  name?: string;
}

export interface CheckBox extends InputGroupBase {
  /** The options to show */
  options: CheckboxProps[];
  /** The type of filter */
  type: FilterInput.CHECKBOX;
}

export interface Radio extends InputGroupBase {
  /** The options to show */
  options: RadiobuttonProps[];
  /** The type of filter */
  type: FilterInput.RADIO;
}

export interface SingleCheckBox extends InputGroupBase {
  /** The icon to render */
  icon?: IconName;
  /** Whether the checkbox is highlighted */
  isHighlighted?: boolean;
  /** The options to show */
  options: CheckboxProps[];
  /** The type of filter */
  type: FilterInput.SINGLE_CHECKBOX;
}

export interface Slider extends InputGroupBase {
  /** The options to show */
  options: RangeSliderProps[];
  /** The type of filter */
  type: FilterInput.SLIDER;
}

export type Filter = CheckBox | Radio | Slider | SingleCheckBox;

interface FilterSlideInInputGroupProps {
  /** The filter to show */
  filter: Filter;
  /** Whether the component is rendered in mobile view */
  isTablet?: boolean;
  /** The number of items to show before collapsing */
  overflowSize?: number;
}

const FilterSlideInInputGroup = ({
  filter,
  isTablet = false,
  overflowSize = 5,
}: FilterSlideInInputGroupProps) => {
  const { label, options, type: filterType, id } = filter;
  const icon =
    filterType === FilterInput.SINGLE_CHECKBOX
      ? (filter as SingleCheckBox).icon
      : undefined;
  const isHighlighted =
    filterType === FilterInput.SINGLE_CHECKBOX
      ? (filter as SingleCheckBox).isHighlighted
      : undefined;
  const { t } = useI18n();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const calculatePillCount = useMemo(() => {
    let count = 0;

    if (filterType === FilterInput.SLIDER) {
      const sliderOption = options[0];

      if (sliderOption.value !== sliderOption.maxValue) {
        count = 1;
      }
    } else if (filterType === FilterInput.CHECKBOX) {
      count = (options as CheckboxProps[]).filter(
        option => option.checked,
      ).length;
    } else if (filterType === FilterInput.RADIO) {
      count = (options as CheckboxProps[]).filter(
        option => option.checked && option.id !== null,
      ).length;
    }

    return count;
  }, [filterType, options]);

  const motionProps = isTablet
    ? {}
    : {
        exit: { height: 0 },
        initial: { height: 0 },
      };

  // A plain function, not a component, as a component declared here would be
  // a new type on every render, remounting the inputs and dropping their focus
  const renderInput = (
    option: Filter['options'][number],
    optionIndex: number,
  ) => {
    if (filterType === FilterInput.CHECKBOX) {
      return <Checkbox key={optionIndex} {...(option as CheckboxProps)} />;
    }

    if (filterType === FilterInput.RADIO) {
      return (
        <Radiobutton key={optionIndex} {...(option as RadiobuttonProps)} />
      );
    }

    if (filterType === FilterInput.SLIDER) {
      // The slider stays mounted, so it must follow value changes such as a
      // reset or switching between total and per person prices
      return (
        <RangeSlider
          key={optionIndex}
          {...(option as RangeSliderProps)}
          shouldUpdateValue
        />
      );
    }

    if (filterType === FilterInput.SINGLE_CHECKBOX && !isHighlighted) {
      return (
        <FilterSlideInSingleCheckbox key={optionIndex} option={options[0]} />
      );
    }

    return null;
  };

  const inputsRender = options.map(renderInput);

  const hasOverflowItems = inputsRender.slice(overflowSize).length > 0;

  const showOverflowItems = isCollapsed && hasOverflowItems;

  const renderLabel = useMemo(() => {
    const isDualChoice = typeof label === 'object';

    const { label: labelText, dualChoiceProps } = isDualChoice
      ? label
      : { dualChoiceProps: undefined, label };

    return (
      <div className={styles.label}>
        <button
          className={styles.labelText}
          onClick={() =>
            !isTablet ? setIsCollapsed(previous => !previous) : undefined
          }
        >
          <Typography tagStyle="bodyMediumBold">{labelText}</Typography>

          {!isTablet && calculatePillCount !== 0 && (
            <Pill count={calculatePillCount} variant="secondary" />
          )}
        </button>

        {isDualChoice && <DualChoice {...dualChoiceProps} type="button" />}

        <button
          className={styles.collapseMobile}
          onClick={() => setIsCollapsed(previous => !previous)}
        >
          <Icon name={isCollapsed ? 'ChevronUp' : 'ChevronDown'} />
        </button>
      </div>
    );
  }, [label, isTablet, calculatePillCount, isCollapsed]);

  return (
    <div
      className={classNames(styles.component, {
        [styles.isMobile]: !isTablet,
      })}
      id={id}
    >
      {!isHighlighted && renderLabel}

      {filterType === FilterInput.SINGLE_CHECKBOX && isHighlighted && (
        <FilterSlideInSingleCheckbox
          icon={icon}
          isHighlighted={isHighlighted}
          option={options[0]}
        />
      )}

      <m.div
        key={isTablet ? 'large-screen' : 'small-screen'}
        animate={{ height: isTablet || isCollapsed ? 'auto' : 0 }}
        className={classNames(styles.inputContainer, styles.inputs, {
          [styles.isCollapsed]: isCollapsed,
        })}
        {...motionProps}
        // Collapsed inputs are only hidden visually, inert keeps them out of
        // the tab order. Spread, as the React 18 types don't know inert yet
        {...(!isTablet && !isCollapsed && { inert: true })}
        transition={{ duration: 0.25 }}
      >
        {inputsRender.slice(0, overflowSize)}

        <m.div
          animate={{ height: showOverflowItems ? 'auto' : 0 }}
          className={classNames(styles.inputs, styles.overflowInputs, {
            [styles.hideOverflowItems]: !hasOverflowItems,
            [styles.isCollapsed]: isCollapsed,
            [styles.showOverflowItems]: isTablet && showOverflowItems,
          })}
          style={{ height: 0 }}
          {...(!showOverflowItems && { inert: true })}
        >
          {inputsRender.slice(overflowSize)}
        </m.div>
      </m.div>

      {inputsRender.length > overflowSize && (
        <button
          className={styles.collapse}
          onClick={() => setIsCollapsed(previous => !previous)}
        >
          <Typography tagStyle="bodyMedium">
            {isCollapsed
              ? t('items.search.filterSlideIn.showLess')
              : t('items.search.filterSlideIn.showMore', {
                  number: inputsRender.length - overflowSize,
                })}
          </Typography>
        </button>
      )}
    </div>
  );
};

FilterSlideInInputGroup.displayName = 'FilterSlideInInputGroup';

export default FilterSlideInInputGroup;
